import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  Linkedin,
  Target,
  TrendingUp,
  UserPlus,
  Activity,
  CheckCircle2,
  MessageCircle,
  AlertTriangle,
} from "lucide-react";
import { format, startOfMonth, subDays } from "date-fns";
import { DailyLimitsCard } from "@/components/dashboard/DailyLimitsCard";
import { ForecastingCard } from "@/components/dashboard/ForecastingCard";

export default function WorkspaceOwnerDashboard() {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();

  // Workspace members query
  const { data: members } = useQuery({
    queryKey: ["workspace-members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("workspace_members")
        .select("*, profiles!user_id(email, full_name, avatar_url)")
        .eq("workspace_id", workspace?.id || "")
        .order("joined_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspace?.id,
  });

  // Clients query
  const { data: clients } = useQuery({
    queryKey: ["workspace-clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("workspace_id", workspace?.id || "")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspace?.id,
  });

  // Campaigns query with stats
  const { data: campaigns } = useQuery({
    queryKey: ["workspace-campaigns"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select(`
          *,
          clients(email, linkedin_name),
          targets(status, connection_accepted_at, reply_received_at, created_at)
        `)
        .eq("workspace_id", workspace?.id || "")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspace?.id,
  });

  // Subscription query for renewal reminder
  const { data: subscription } = useQuery({
    queryKey: ["workspace-subscription", workspace?.id],
    queryFn: async () => {
      // Account seats are the only renewal source: nearest active seat renewal.
      const { data, error } = await supabase
        .from("account_subscriptions" as any)
        .select("current_period_end, status, billing_cycle")
        .eq("workspace_id", workspace?.id || "")
        .eq("status", "active")
        .not("current_period_end", "is", null)
        .order("current_period_end", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!workspace?.id,
  });

  // Check if enterprise features are enabled
  const { data: workspaceSettings } = useQuery({
    queryKey: ["workspace-enterprise-settings", workspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("workspaces")
        .select("enterprise_features_enabled")
        .eq("id", workspace?.id || "")
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!workspace?.id,
  });

  const enterpriseEnabled = workspaceSettings?.enterprise_features_enabled || false;

  // Calculate aggregate stats
  const activeClients = clients?.filter((c) => c.status === "active").length || 0;
  const disconnectedClients = clients?.filter((c) => c.status === "disconnected").length || 0;
  const activeCampaigns = campaigns?.filter((c) => c.status === "active").length || 0;

  // Calculate collective campaign results
  const allTargets = campaigns?.flatMap((c) => c.targets || []) || [];
  const totalTargets = allTargets.length;
  const connectionsSent = allTargets.filter((t: any) => t.status !== 'pending' && t.status !== 'visited').length;
  const connectionsAccepted = allTargets.filter((t) => t.connection_accepted_at).length;
  const repliesReceived = allTargets.filter((t) => t.reply_received_at).length;
  const acceptanceRate = connectionsSent > 0 ? (connectionsAccepted / connectionsSent) * 100 : 0;
  const replyRate = connectionsAccepted > 0 ? (repliesReceived / connectionsAccepted) * 100 : 0;

  // Current month data for forecasting
  const monthStart = startOfMonth(new Date());
  const currentMonthTargets = allTargets.filter((t) => {
    // If target has a created_at, use it; otherwise assume it's in current month
    return true; // We'll count all targets for now since we need the total reached
  }).length;

  const currentMonthConnections = allTargets.filter((t) => {
    if (!t.connection_accepted_at) return false;
    return new Date(t.connection_accepted_at) >= monthStart;
  }).length;

  const currentMonthReplies = allTargets.filter((t) => {
    if (!t.reply_received_at) return false;
    return new Date(t.reply_received_at) >= monthStart;
  }).length;

  // Calculate forecasting data per client
  const clientForecasts = clients
    ?.filter(c => c.status === 'active' || c.status === 'syncing')
    .map(client => {
      // Get campaigns for this client
      const clientCampaigns = campaigns?.filter(c => c.client_id === client.id) || [];
      const clientTargets = clientCampaigns.flatMap(c => c.targets || []);

      const sent = clientTargets.filter((t: any) => t.status !== 'pending' && t.status !== 'visited').length;
      const accepted = clientTargets.filter(t => t.connection_accepted_at).length;

      const actualRate = sent > 0 ? (accepted / sent * 100) : 0;

      // Use 25% baseline, or actual if higher
      const forecastRate = actualRate > 25 ? actualRate : 25;

      const dailyCapacity = client.is_premium ? 25 : 20;

      return {
        id: client.id,
        name: client.linkedin_name || "LinkedIn Member",
        email: client.email,
        avatar_url: client.profile_picture_url,
        is_premium: client.is_premium,
        dailyCapacity,
        forecastRate,
        actualRate
      };
    }) || [];

  // Trial/subscription info

  // Trial/subscription info
  const isTrial = workspace?.subscription_status === "trial";
  const isFree = workspace?.subscription_tier === "free" && !isTrial;
  const trialEndsAt = workspace?.trial_ends_at ? new Date(workspace.trial_ends_at) : null;
  const daysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  // Seats: show 0 for free tier, actual usage for trial/paid
  const displaySeats = isFree ? 0 : (workspace?.used_seats || 0);
  const displayMaxSeats = isFree ? 0 : (workspace?.max_seats === 999999 ? "∞" : workspace?.max_seats || 0);
  const seatUsagePercentage = (!isFree && workspace?.max_seats && workspace.max_seats !== 999999)
    ? ((workspace.used_seats || 0) / workspace.max_seats) * 100
    : 0;

  // Subscription renewal reminder (7 days before expiry)
  const renewalDate = (subscription as any)?.current_period_end
    ? new Date((subscription as any).current_period_end)
    : null;
  const daysUntilRenewal = renewalDate
    ? Math.ceil((renewalDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  // Simple date-based expiry detection
  const isExpired = (renewalDate && renewalDate < new Date()) ||
    (subscription as any)?.status === 'expired' ||
    workspace?.subscription_status === 'expired';

  const showRenewalBanner = !isTrial && !isFree && daysUntilRenewal !== null && daysUntilRenewal <= 7 && daysUntilRenewal > 0;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Simple Header with Status Badge */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {isTrial
              ? `Trial${daysLeft !== null ? ` • ${daysLeft}d left` : ''}`
              : isFree
                ? 'No plan'
                : daysUntilRenewal !== null && daysUntilRenewal > 0
                  ? `Active • ${daysUntilRenewal}d until renewal`
                  : workspace?.subscription_tier || 'Active'}
          </Badge>
          {(isTrial || isFree) && (
            <Button size="sm" onClick={() => navigate('/billing')}>
              Subscribe
            </Button>
          )}
        </div>
      </div>

      {/* Expired Banner */}
      {isExpired && (
        <div className="relative overflow-hidden bg-destructive/5 border border-destructive/20 rounded-2xl p-6">
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-destructive/10 rounded-xl">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-destructive text-lg">Subscription Expired</h3>
                <p className="text-muted-foreground">
                  Your subscription has expired. Please renew to access your campaigns and accounts.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/billing')}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground shadow-lg whitespace-nowrap"
              size="lg"
            >
              Renew Now
            </Button>
          </div>
        </div>
      )}

      {/* Renewal Reminder Banner - 7 days before expiry */}
      {!isExpired && showRenewalBanner && (
        <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200 dark:border-amber-800 rounded-2xl p-6">
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/50 rounded-xl">
                <TrendingUp className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900 dark:text-amber-100 text-lg">Subscription Renewing Soon</h3>
                <p className="text-amber-700 dark:text-amber-300">
                  Your plan expires in {daysUntilRenewal} day{daysUntilRenewal !== 1 ? 's' : ''} ({renewalDate && format(renewalDate, 'PPP')}).
                  Renew now to avoid any interruption to your campaigns.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/billing')}
              className="bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-900/10 whitespace-nowrap"
              size="lg"
            >
              Renew Now
            </Button>
          </div>
        </div>
      )}

      {/* Key Business Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Team & Seats */}
        <Card className="border-sidebar-border/60 shadow-sm bg-card/50 backdrop-blur-sm hover:shadow-md transition-all">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <Users className="h-5 w-5" />
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Seats</p>
                <p className="text-sm font-semibold">
                  {isFree ? "No subscription" : `${displaySeats}/${displayMaxSeats}`}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold tracking-tight">{members?.length || 0}</h3>
              <p className="text-sm text-muted-foreground">Team Members</p>
              {!isFree && workspace?.max_seats !== 999999 && (
                <Progress value={seatUsagePercentage} className="h-1.5" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* LinkedIn Accounts */}
        <Card className="border-sidebar-border/60 shadow-sm bg-card/50 backdrop-blur-sm hover:shadow-md transition-all">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-linkedin/10 rounded-lg text-linkedin">
                <Linkedin className="h-5 w-5" />
              </div>
              <span className="text-xs font-medium text-linkedin bg-linkedin/10 px-2 py-1 rounded-full">
                {activeClients} Active
              </span>
            </div>
            <div className="space-y-1">
              <h3 className="text-2xl font-bold tracking-tight">{clients?.length || 0}</h3>
              <p className="text-sm text-muted-foreground">Connected Accounts</p>
              {disconnectedClients > 0 && (
                <p className="text-xs text-warning">{disconnectedClients} disconnected</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Campaign Performance */}
        <Card className="border-sidebar-border/60 shadow-sm bg-card/50 backdrop-blur-sm hover:shadow-md transition-all">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-600">
                <Target className="h-5 w-5" />
              </div>
              <span className="text-xs font-medium text-emerald-600 bg-emerald-500/10 px-2 py-1 rounded-full">
                {activeCampaigns} Running
              </span>
            </div>
            <div className="space-y-1">
              <h3 className="text-2xl font-bold tracking-tight">{campaigns?.length || 0}</h3>
              <p className="text-sm text-muted-foreground">Total Campaigns</p>
              <p className="text-xs text-muted-foreground">{totalTargets} targets</p>
            </div>
          </CardContent>
        </Card>

        {/* Results Summary */}
        <Card className="border-sidebar-border/60 shadow-sm bg-card/50 backdrop-blur-sm hover:shadow-md transition-all">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-xs text-muted-foreground">Connections</span>
                </div>
                <p className="text-lg font-bold">{connectionsAccepted}</p>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs text-muted-foreground">Replies</span>
                </div>
                <p className="text-lg font-bold">{repliesReceived}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <ForecastingCard clients={clientForecasts} />

      {/* Daily Limits & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily LinkedIn Limits */}
        <DailyLimitsCard />

        {/* Quick Actions */}
        <Card className="border-sidebar-border/60 shadow-sm bg-card/50 backdrop-blur-sm">
          <CardHeader className="pb-3 border-b border-sidebar-border/60">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/clients" className="group">
                <Button variant="outline" className="w-full h-auto py-3 px-4 justify-start gap-3 hover:bg-accent hover:border-primary/30 transition-all">
                  <div className="p-2 bg-linkedin/10 rounded-md text-linkedin group-hover:bg-linkedin/20 transition-colors">
                    <Linkedin className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">Connect Account</div>
                    <div className="text-xs text-muted-foreground font-normal">Add LinkedIn profiles</div>
                  </div>
                </Button>
              </Link>

              <Link to={workspace?.settings?.simplified_onboarding_enabled ? "/campaigns/managed" : "/campaigns/new"} className="group">
                <Button variant="outline" className="w-full h-auto py-3 px-4 justify-start gap-3 hover:bg-accent hover:border-primary/30 transition-all">
                  <div className="p-2 bg-primary/10 rounded-md text-primary group-hover:bg-primary/20 transition-colors">
                    <Target className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">New Campaign</div>
                    <div className="text-xs text-muted-foreground font-normal">Start outreach sequence</div>
                  </div>
                </Button>
              </Link>

              <Link to="/campaigns" className="group">
                <Button variant="outline" className="w-full h-auto py-3 px-4 justify-start gap-3 hover:bg-accent hover:border-primary/30 transition-all">
                  <div className="p-2 bg-primary/10 rounded-md text-primary group-hover:bg-primary/20 transition-colors">
                    <Activity className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">View Campaigns</div>
                    <div className="text-xs text-muted-foreground font-normal">Monitor progress</div>
                  </div>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
